import { useCallback, useMemo } from 'react';
import { TrackService } from '@/services/track.service';
import { useFirestoreSubscription } from './useFirestoreSubscription';
import type { Track, VoteValue } from '@/types';
import { useAuth } from './useAuth';

interface UseTracksResult {
    tracks: Track[];
    userVotes: Record<string, VoteValue>;
    isLoading: boolean;
    error: Error | null;
    addTrack: (track: Omit<Track, 'addedAt' | 'lastVotedAt'>) => Promise<void>;
    removeTrack: (trackId: string) => Promise<void>;
    voteTrack: (trackId: string, value: VoteValue) => Promise<void>;
}

export function useTracks(playlistId: string | undefined): UseTracksResult {
    const { user } = useAuth();

    const { data, isLoading, error } = useFirestoreSubscription<Track[]>(
        (callback) => {
            if (!playlistId) {
                callback([]);
                return () => { };
            }
            return TrackService.subscribeToTracks(playlistId, callback);
        },
        [playlistId]
    );

    const tracks = data || [];

    // Derive user votes from tracks
    const userVotes = useMemo(() => {
        if (!user) return {};
        const votes: Record<string, VoteValue> = {};
        tracks.forEach(track => {
            if (track.votes && track.votes[user.id]) {
                votes[track.id] = track.votes[user.id] as VoteValue;
            }
        });
        return votes;
    }, [tracks, user]);

    const addTrack = useCallback(async (trackData: Omit<Track, 'addedAt' | 'lastVotedAt'>) => {
        if (!playlistId || !user) return;
        await TrackService.addTrack(playlistId, trackData, user.id);
    }, [playlistId, user]);

    const removeTrack = useCallback(async (trackId: string) => {
        if (!playlistId) return;
        await TrackService.removeTrack(playlistId, trackId);
    }, [playlistId]);

    const voteTrack = useCallback(async (trackId: string, value: VoteValue) => {
        if (!playlistId || !user) return;
        await TrackService.voteTrack(playlistId, trackId, user.id, value);
    }, [playlistId, user]);

    return {
        tracks,
        userVotes,
        isLoading,
        error,
        addTrack,
        removeTrack,
        voteTrack
    };
}
